import { useMemo } from 'react';
import { Card } from '../ui/card';
import { Calendar as CalendarIcon } from 'lucide-react';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const VISIBLE_TIME_START = '07:30';
const VISIBLE_TIME_END = '19:30';
const AVAIL_VALUES = [
  'Available (In-Clinic)', 'Available (Outside)', 'Available (In & Out)',
  'Limited (In-Clinic)', 'Limited (Outside)', 'Full', 'Hold', 'Not Offered',
];
const AVAIL_COLORS = {
  'Available (In-Clinic)': 'bg-[#839E97] text-white',
  'Available (Outside)':   'bg-[#BAA9CA] text-[#2D2833]',
  'Available (In & Out)':  'bg-[#6D857E] text-white',
  'Limited (In-Clinic)':   'bg-[#B08A3A]/70 text-[#2D2833]',
  'Limited (Outside)':     'bg-[#B08A3A]/40 text-[#2D2833]',
  'Full':                  'bg-[#685B72] text-white',
  'Hold':                  'bg-[#8A3A3A] text-white',
  'Not Offered':           'bg-[#EFEDF2] text-[#685B72]',
};

/**
 * 27×6 weekly availability grid — one row per time slot (visible window
 * 07:30 → 19:30), one column per weekday. Each cell is a native <select>
 * so admins can flip its status inline.
 */
export default function AvailabilityGrid({ clinic, canEdit, updateCell }) {
  // Memoize the visible time slots so the filter doesn't re-run on every
  // cell-click render (27 rows filtered from ~30, cheap but adds up when
  // combined with 27×6 = 162 selects re-rendering).
  const visibleTimes = useMemo(
    () => (clinic.availability_times || []).filter(t => t >= VISIBLE_TIME_START && t <= VISIBLE_TIME_END),
    [clinic.availability_times],
  );
  return (
    <Card className="rounded-2xl border-[#E8E5EC] p-6" data-testid="availability-grid">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h3 className="font-serif text-xl text-[#2D2833] flex items-center gap-2">
            <CalendarIcon size={18} /> Weekly scheduling availability
          </h3>
          <div className="text-xs text-[#685B72] mt-0.5">Click any cell to change status · updates live for all admin staff</div>
        </div>
        <div className="flex flex-wrap gap-1.5 text-[10px]">
          {AVAIL_VALUES.map(v => <span key={v} className={`px-2 py-0.5 rounded-full ${AVAIL_COLORS[v]}`}>{v}</span>)}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className="p-1.5 text-left text-[#685B72] font-semibold">Time</th>
              {DAYS.map(d => <th key={d} className="p-1.5 text-center text-[#685B72] font-semibold">{d}</th>)}
            </tr>
          </thead>
          <tbody>
            {visibleTimes.map(t => (
              <tr key={t}>
                <td className="p-1.5 text-[#685B72] font-mono">{t}</td>
                {DAYS.map(d => {
                  const val = clinic.availability_grid?.[t]?.[d] || 'Not Offered';
                  return (
                    <td key={d} className="p-0.5">
                      <select
                        value={val}
                        onChange={e => canEdit && updateCell(t, d, e.target.value)}
                        disabled={!canEdit}
                        className={`w-full text-[10px] rounded-md px-1.5 py-1 border-0 focus:ring-2 focus:ring-[#685B72] cursor-pointer ${AVAIL_COLORS[val] || AVAIL_COLORS['Not Offered']}`}
                        data-testid={`slot-${t}-${d}`}
                      >
                        {AVAIL_VALUES.map(v => <option key={v} value={v} className="bg-white text-[#2D2833]">{v}</option>)}
                      </select>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
